import { Params } from '@remix-run/react';
import { Config } from '@travelwin/core';
import { Auth, withSSRContext } from 'aws-amplify';
import cookie from 'cookie';
import { unique } from 'radash';

import { RichTextContent } from '@/interfaces/cms';

import i18n from '../../app/i18n';
import { ecommerceConfigSchema } from '../components/EcommerceConfigProvider/ecommerceConfigSchema';
import { CURRENCY_DEFAULT, CURRENCY_STORAGE_KEY } from '../constants';
import { request } from '../services/helpers/requests';
import {
  isLexicalBlockNode,
  traverseLexicalNodes,
} from '../utils/lexicalUtilts';

export const isRemix = () => {
  return (
    typeof window === 'undefined' || typeof __remixManifest !== 'undefined'
  );
};

export const isClient = () => {
  return typeof window !== 'undefined';
};

export const getUniteBaseUrl = () => {
  if (typeof window === 'undefined') {
    return process.env.REACT_APP_API_ENDPOINT_ECOMMERCE;
  } else {
    return window.ENV.REACT_APP_API_ENDPOINT_ECOMMERCE;
  }
};

export const getCmsBaseUrl = () => {
  if (typeof window === 'undefined') {
    return process.env.REACT_APP_CMS_ENDPOINT;
  } else {
    return window.ENV.REACT_APP_CMS_ENDPOINT;
  }
};

// used in deployed environments to point to CloudFront directly
// instead routing images through the CMS
export const getCmsMediaBaseUrl = () => {
  if (typeof window === 'undefined') {
    return process.env.REACT_APP_CMS_MEDIA_ENDPOINT;
  } else {
    return window.ENV.REACT_APP_CMS_MEDIA_ENDPOINT;
  }
};

const getAuthInstance = (request: Request): typeof Auth => {
  const Amplify = withSSRContext({
    req: { headers: { cookie: request.headers.get('Cookie') } },
  });

  return Amplify.Auth;
};

export const getUser = async (request: Request) => {
  try {
    const auth = getAuthInstance(request);

    const user = await auth.currentAuthenticatedUser();

    return user;
  } catch {
    return null;
  }
};

export const getCurrency = ({
  request,
  config,
}: {
  request: Request;
  config: Config;
}) => {
  const cookieHeader = request.headers.get('Cookie');
  const cookieValue = cookieHeader
    ? cookie.parse(cookieHeader)[CURRENCY_STORAGE_KEY]
    : undefined;

  if (
    cookieValue &&
    config.PAYMENT_SUPPORTED_CURRENCIES?.includes(cookieValue)
  ) {
    return cookieValue;
  }

  return (
    config.PAYMENT_DEFAULT_CURRENCY ??
    config.PAYMENT_SUPPORTED_CURRENCIES?.at(0) ??
    CURRENCY_DEFAULT
  );
};

export const getConfig = async () => {
  const response = await request('settings/ui_settings');

  const config = await ecommerceConfigSchema.validate(response);

  return config;
};

export const assertValidLang = (params: Params) => {
  const isInvalidLang =
    params.lang &&
    (params.lang === 'en' || !i18n.supportedLngs.includes(params.lang));

  if (isInvalidLang) {
    throw new Response(null, { status: 404, statusText: 'Not Found' });
  }
};

export const extractPlanIds = (content: RichTextContent) => {
  const planIds: string[] = [];

  traverseLexicalNodes(content.root, (node) => {
    if (isLexicalBlockNode(node) && node.fields.blockType === 'plan-card') {
      planIds.push(node.fields.planId);
    } else if (
      isLexicalBlockNode(node) &&
      node.fields.blockType === 'plan-price'
    ) {
      planIds.push(node.fields.planId);
    }
  });

  return unique(planIds);
};

export const getServerUserAgent = (request: Request) => {
  return request.headers.get('user-agent') || 'Unknown';
};
